import type { ProgramOutcome } from '@@/api/program';
import type { LearningsPageResponse } from '@@/api/learnings/page/page-types';
import type { PatternDetail, Tone } from '@/state/learnings/page-view';
import type { LearningsPageManager } from '@/state/learnings/useLearningsPage';
import type { LearningsScreenManager } from '@/state/learnings/useLearningsScreen';
import { Button } from '@/components/ui/button';
import { cn } from 'helpers/cn';
import { OUTCOME_LABELS } from '@/state/experiments/library-learnings';
import {
  axisTicks,
  barGeometry,
  pageLabel,
  patternDetail,
  patternTag,
  scaleFor,
  signedPercent,
  signedRange,
  testsOfPattern,
} from '@/state/learnings/page-view';
import { ActionButton, EmptyState, FOCUS_RING, MetricTile, MICRO_LABEL_SHAPE } from '../../ConsolePanel';
import { Axis, IntervalBar, TagPill, ToneChip } from './parts';

const OUTCOME_TONE: Record<ProgramOutcome, Tone> = {
  won: 'pos',
  lost: 'neg',
  mixed: 'warn',
  inconclusive: 'dim',
  'no-data': 'dim',
};

const GROUPING_LABEL = { cell: 'Page', theme: 'Theme', element: 'Element', mechanism: 'Mechanism' } as const;

type PatternViewProps = {
  manager: LearningsPageManager;
  page: LearningsPageResponse;
  screen: LearningsScreenManager;
  patternKey: string;
};

/** One learning: its estimate test by test, what the tests show, why it may be, and what to do next. */
export const PatternView = ({ manager, page, screen, patternKey }: PatternViewProps) => {
  const detail = patternDetail(page, patternKey);

  return (
    <div className="flex flex-wrap items-start gap-[14px]">
      <LearningList page={page} selected={patternKey} onSelect={screen.openPattern} />
      <section className="bg-panel border-line rounded-panel min-w-0 flex-[3_1_520px] overflow-hidden border">
        {detail ? (
          <Detail detail={detail} page={page} manager={manager} screen={screen} />
        ) : (
          <div className="p-3.5">
            <EmptyState>This pattern is no longer in the evidence: the tests behind it have changed.</EmptyState>
          </div>
        )}
      </section>
    </div>
  );
};

type LearningListProps = { page: LearningsPageResponse; selected: string; onSelect: (key: string) => void };

const LearningList = ({ page, selected, onSelect }: LearningListProps) => (
  <section className="bg-panel border-line rounded-panel min-w-0 max-w-[320px] flex-[1_1_240px] overflow-hidden border">
    <div className="bg-panel2 border-line border-b px-3.5 py-2.5">
      <span className="micro-label">Learnings · {page.featured.length}</span>
    </div>
    {page.featured.map((pattern) => {
      const { tag, tone } = patternTag(pattern);
      const isSelected = pattern.key === selected;
      return (
        <button
          key={pattern.key}
          type="button"
          onClick={() => onSelect(pattern.key)}
          aria-current={isSelected || undefined}
          className={cn(
            'border-line hover:bg-hover flex w-full flex-col items-start gap-1.5 border-b px-3.5 py-2.5 text-left transition-colors duration-150',
            isSelected ? 'bg-sel-bg shadow-[inset_2px_0_0_hsl(var(--sel))]' : 'bg-transparent',
            FOCUS_RING,
          )}
        >
          <ToneChip tone={tone} className="text-[9px]">
            {tag}
          </ToneChip>
          <span className="text-meta font-medium text-pretty">{pattern.wording?.title ?? pattern.label}</span>
        </button>
      );
    })}
  </section>
);

type DetailProps = {
  detail: PatternDetail;
  page: LearningsPageResponse;
  manager: LearningsPageManager;
  screen: LearningsScreenManager;
};

const Detail = ({ detail, page, manager, screen }: DetailProps) => {
  const { tag, tone } = patternTag(detail);
  const tests = testsOfPattern(detail.members);
  const pooled = detail.pooled;
  const scale = scaleFor([
    ...(pooled ? [pooled.lower, pooled.upper] : []),
    ...detail.members.flatMap((member) => [member.liftLower ?? 0, member.liftUpper ?? 0]),
  ]);
  const crossesZero = !!pooled && pooled.lower < 0 && pooled.upper > 0;
  const isKnown = detail.feedback === 'known';

  return (
    <>
      <div
        className="bg-panel2 border-line flex flex-col gap-2 border-b px-3.5 py-3"
        data-tour="learnings-pattern-head"
      >
        <div className="flex flex-wrap gap-1.5">
          <ToneChip tone={tone}>{tag}</ToneChip>
          <ToneChip tone="dim">{pageLabel(detail.pageType)}</ToneChip>
          <ToneChip tone="dim">{GROUPING_LABEL[detail.grouping]}</ToneChip>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="m-0 text-[18px] font-medium tracking-[-0.01em]">{detail.wording?.title ?? detail.label}</h1>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={manager.isSavingFeedback}
              onClick={() => manager.setFeedback(detail.key, isKnown ? null : 'known')}
            >
              {isKnown ? 'Known · undo' : 'Mark as known'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={manager.isSavingFeedback}
              onClick={() => manager.setFeedback(detail.key, 'not-useful')}
            >
              Not useful
            </Button>
            <Button size="sm" onClick={() => screen.openTab('opportunities')}>
              Test this further
            </Button>
          </div>
        </div>
      </div>

      <div className="border-line border-b p-3.5">
        <div className="grid gap-[7px] [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
          <MetricTile
            label="Pooled lift"
            value={pooled ? signedPercent(pooled.mean) : 'No reading'}
            valueClassName={tone === 'pos' ? 'text-ok' : tone === 'neg' ? 'text-danger' : undefined}
          />
          <MetricTile label="Range" value={pooled ? signedRange(pooled.lower, pooled.upper) : 'None'} />
          <MetricTile label="Tests" value={String(detail.tests)} />
          <MetricTile
            label="Shared with"
            value={`${detail.sameTestsAs.length} ${detail.sameTestsAs.length === 1 ? 'row' : 'rows'}`}
          />
        </div>
      </div>

      <div className="border-line flex flex-col gap-1 border-b p-3.5" data-tour="learnings-estimate">
        <span className="micro-label mb-1.5">Estimate</span>
        {pooled && (
          <EstimateRow label="Pooled" strong value={signedPercent(pooled.mean)}>
            <IntervalBar
              bar={barGeometry({ mean: pooled.mean, lower: pooled.lower, upper: pooled.upper, scale })}
              tone={tone}
              label={`Pooled ${signedPercent(pooled.mean)}`}
            />
          </EstimateRow>
        )}
        {detail.members.map((member) => {
          const hasRange = member.liftLower !== null && member.liftUpper !== null;
          const mid = hasRange ? ((member.liftLower ?? 0) + (member.liftUpper ?? 0)) / 2 : 0;
          return (
            <EstimateRow
              key={member.variantId}
              label={`${member.name} · ${member.variantName}`}
              value={hasRange ? signedPercent(mid) : 'No reading'}
            >
              {hasRange && (
                <IntervalBar
                  bar={barGeometry({ mean: mid, lower: member.liftLower, upper: member.liftUpper, scale })}
                  tone="mix"
                  label={`${member.name}: ${signedPercent(mid)}`}
                />
              )}
            </EstimateRow>
          );
        })}
        <div className="grid gap-3.5 [grid-template-columns:minmax(120px,200px)_minmax(0,1fr)_64px]">
          <span />
          <Axis ticks={axisTicks(scale)} />
          <span />
        </div>
        <p className="text-dim mt-2 max-w-[80ch] text-meta text-pretty">
          The true effect most likely sits inside the band.
          {crossesZero && ' It still reaches across zero, so the opposite effect is possible.'} Bands are adjusted for
          the many patterns checked at once.
        </p>
      </div>

      <div className="border-line grid border-b [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]">
        <div className="border-line flex flex-col gap-1.5 border-r p-3.5">
          <span className="micro-label">What the results show</span>
          <span className="text-meta text-pretty">
            {detail.wording?.description ??
              `${detail.tests} ${detail.tests === 1 ? 'test' : 'tests'} pooled on each test's primary goal${pooled ? `: ${signedPercent(pooled.mean)}` : ''}.`}
          </span>
        </div>
        <div className="flex flex-col gap-1.5 p-3.5">
          <span className="micro-label">Interpretation · AI, not reviewed</span>
          <span className="text-meta text-pretty">
            {detail.wording?.interpretation ?? 'Written once this pattern is featured and the next analysis has run.'}
          </span>
        </div>
      </div>

      <div
        className="border-line flex items-center justify-between border-b px-3.5 py-2.5"
        data-tour="learnings-contributing"
      >
        <span className="micro-label">Contributing tests · {tests.length}</span>
        <button
          type="button"
          onClick={() => screen.openTab('evidence')}
          className={cn('text-accent text-meta hover:underline', FOCUS_RING)}
        >
          Open in Evidence
        </button>
      </div>
      {tests.map((test) => {
        const note = page.tests[test.experimentId];
        return (
          <div key={test.experimentId} className="border-line flex items-start gap-3.5 border-b px-3.5 py-3">
            <div className="flex shrink-0 gap-1">
              <Thumb label="A" url={note?.thumbnails.control ?? null} />
              <Thumb label="B" url={note?.thumbnails.variant ?? null} />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="num text-dim">#{test.experimentId}</span>
                <span className="text-meta font-medium">{test.name}</span>
                <ToneChip tone={OUTCOME_TONE[test.outcome]} className="text-[9px]">
                  {OUTCOME_LABELS[test.outcome]}
                </ToneChip>
              </div>
              {note?.learning && <span className="text-meta">{note.learning}</span>}
            </div>
            <ActionButton onClick={() => screen.openTest(test.experimentId)} ariaLabel={`Debrief of ${test.name}`}>
              Debrief
            </ActionButton>
          </div>
        );
      })}

      {detail.sameTestsAs.length > 0 && (
        <div className="border-line flex flex-col gap-2 border-b px-3.5 py-3">
          <span className="micro-label">Same tests, also counted in</span>
          <div className="flex flex-wrap gap-1.5">
            {detail.sameTestsAs.map((ref) => (
              <TagPill key={ref.key} dashed>
                {ref.label}
              </TagPill>
            ))}
          </div>
          <span className="supporting">Agreement with these is the same evidence seen twice, not a confirmation.</span>
        </div>
      )}

      {detail.wording?.nextStep && (
        <div className="bg-sel-bg flex flex-wrap items-center justify-between gap-3 px-3.5 py-3">
          <div className="flex flex-col gap-0.5">
            <span className={cn(MICRO_LABEL_SHAPE, 'text-sel')}>Next</span>
            <span className="text-body font-medium">{detail.wording.nextStep}</span>
          </div>
          <Button size="sm" onClick={() => screen.openTab('opportunities')}>
            Test this further
          </Button>
        </div>
      )}
    </>
  );
};

type EstimateRowProps = { label: string; value: string; strong?: boolean; children?: React.ReactNode };

const EstimateRow = ({ label, value, strong = false, children }: EstimateRowProps) => (
  <div className="border-line grid items-center gap-3.5 border-b py-[7px] [grid-template-columns:minmax(120px,200px)_minmax(0,1fr)_64px]">
    <span className={cn('truncate text-meta', strong && 'font-medium')}>{label}</span>
    <div>{children}</div>
    <span className="num text-right font-medium">{value}</span>
  </div>
);

type ThumbProps = { label: string; url: string | null };

/** A 64 by 44 screenshot, or its letter on the field colour when the variant has none. */
const Thumb = ({ label, url }: ThumbProps) => (
  <span className="bg-field border-line micro-label relative flex h-11 w-16 items-end overflow-hidden rounded-md border px-1.5 py-[3px]">
    {url && <img src={url} alt="" loading="lazy" className="absolute inset-0 size-full object-cover object-top" />}
    <span className="relative">{label}</span>
  </span>
);
